<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDynamoDbTableStatsHistory,
  useDynamoDbTableStatsLatest,
  useDynamoDbTableNameOptions,
} from '@/composables/api/useDynamoDbTableStatsApi';
import { formatBytes } from '@/utils/formatBytes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const historyDaysString = ref('90');
const historyDays = computed(() => Number.parseInt(historyDaysString.value, 10) || 90);

/** Empty string = all tables (aggregated). */
const historyTableFilter = ref('');

const { data: latestData, isLoading: latestLoading, error: latestError } =
  useDynamoDbTableStatsLatest();

const tableNameOptions = useDynamoDbTableNameOptions(
  computed(() => latestData.value?.tables)
);

const { data: historyData, isLoading: historyLoading, error: historyError } =
  useDynamoDbTableStatsHistory(historyDays, historyTableFilter);

function onChartTableChange(value: unknown) {
  if (value === null || value === undefined) {
    return;
  }
  const v = String(value);
  historyTableFilter.value = v === '__all__' ? '' : v;
}

const chartTableSelectValue = computed(() =>
  historyTableFilter.value === '' ? '__all__' : historyTableFilter.value
);

const latestCapturedLabel = computed(() => {
  const raw = latestData.value?.captured_at;
  if (!raw) {
    return '—';
  }
  return dayjs(raw).format('YYYY-MM-DD HH:mm');
});

const latestTotals = computed(() => {
  const tables = latestData.value?.tables ?? [];
  let items = 0;
  let bytes = 0;
  for (const row of tables) {
    items += Number(row.item_count);
    bytes += Number(row.size_bytes);
  }
  return { tableCount: tables.length, items, bytes };
});

type SeriesPoint = { label: string; items: number; sizeGb: number };

const historySeries = computed((): SeriesPoint[] => {
  const rows = historyData.value?.rows ?? [];
  if (rows.length === 0) {
    return [];
  }

  const byTime = new Map<string, { items: number; sizeBytes: number }>();
  for (const row of rows) {
    const key = row.captured_at;
    const existing = byTime.get(key) ?? { items: 0, sizeBytes: 0 };
    existing.items += Number(row.item_count);
    existing.sizeBytes += Number(row.size_bytes);
    byTime.set(key, existing);
  }

  const sortedKeys = [...byTime.keys()].sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
  );

  return sortedKeys.map((key) => {
    const bucket = byTime.get(key)!;
    return {
      label: dayjs(key).format('MMM D'),
      items: bucket.items,
      sizeGb: bucket.sizeBytes / 1024 ** 3,
    };
  });
});

const itemsChartLabel = computed(() =>
  historyTableFilter.value ? 'Items' : 'Items (all tables)'
);

const itemsChartData = computed(() => ({
  labels: historySeries.value.map((point) => point.label),
  datasets: [
    {
      label: itemsChartLabel.value,
      data: historySeries.value.map((point) => point.items),
      borderColor: 'rgb(37, 99, 235)',
      backgroundColor: 'rgba(37, 99, 235, 0.08)',
      fill: true,
      tension: 0.2,
    },
  ],
}));

const sizeChartData = computed(() => ({
  labels: historySeries.value.map((point) => point.label),
  datasets: [
    {
      label: historyTableFilter.value ? 'Size (GB)' : 'Size (GB, all tables)',
      data: historySeries.value.map((point) => point.sizeGb),
      borderColor: 'rgb(22, 163, 74)',
      backgroundColor: 'rgba(22, 163, 74, 0.08)',
      fill: true,
      tension: 0.2,
    },
  ],
}));

const itemsChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: true,
    },
    tooltip: {
      callbacks: {
        label(context) {
          const y = context.parsed.y;
          if (y === null) {
            return '';
          }
          return `Items: ${Intl.NumberFormat().format(y)}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 0,
        maxTicksLimit: 14,
      },
    },
    y: {
      beginAtZero: true,
    },
  },
};

const sizeChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: true,
    },
    tooltip: {
      callbacks: {
        label(context) {
          const y = context.parsed.y;
          if (y === null) {
            return '';
          }
          return `Storage: ${y.toFixed(2)} GB`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 0,
        maxTicksLimit: 14,
      },
    },
    y: {
      beginAtZero: true,
    },
  },
};
</script>

<template>
  <div class="flex flex-1 flex-col gap-6 p-6 max-md:pb-mobile-area-bar-clearance">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight">DynamoDB table statistics</h1>
      <p class="text-muted-foreground mt-1 max-w-3xl text-sm leading-relaxed">
        Nightly snapshots from <code class="rounded bg-muted px-1 py-0.5 text-xs">dynamo:report-table-stats</code>.
        Charts show item counts and total storage over time. Tables named with
        <code class="rounded bg-muted px-1 py-0.5 text-xs">_YYYYMM</code> are treated as monthly; if AWS-reported
        storage drops between runs, Slack is notified.
      </p>
    </div>

    <div v-if="latestError || historyError" class="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
      <span v-if="latestError">Could not load latest snapshot.</span>
      <span v-if="historyError">Could not load history.</span>
      <span class="text-muted-foreground"> You may need developer access.</span>
    </div>

    <div class="flex flex-wrap items-end gap-4">
      <div class="space-y-2">
        <p class="text-sm font-medium leading-none">History window</p>
        <Select v-model="historyDaysString">
          <SelectTrigger class="w-[180px]">
            <SelectValue placeholder="Days" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
            <SelectItem value="366">Last 366 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div class="space-y-2">
        <p class="text-sm font-medium leading-none">Chart scope</p>
        <Select
          :model-value="chartTableSelectValue"
          @update:model-value="onChartTableChange"
        >
          <SelectTrigger class="w-[280px]">
            <SelectValue placeholder="Tables" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All tables (totals per snapshot)</SelectItem>
            <SelectItem
              v-for="name in tableNameOptions"
              :key="name"
              :value="name"
            >
              {{ name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-medium text-muted-foreground">Last capture</CardTitle>
        </CardHeader>
        <CardContent class="text-lg font-semibold">
          {{ latestLoading ? '…' : latestCapturedLabel }}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-medium text-muted-foreground">Tables</CardTitle>
        </CardHeader>
        <CardContent class="text-lg font-semibold">
          {{ latestLoading ? '…' : latestTotals.tableCount }}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-medium text-muted-foreground">Total items</CardTitle>
        </CardHeader>
        <CardContent class="text-lg font-semibold">
          {{ latestLoading ? '…' : Intl.NumberFormat().format(latestTotals.items) }}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-medium text-muted-foreground">Total size</CardTitle>
        </CardHeader>
        <CardContent class="text-lg font-semibold">
          {{ latestLoading ? '…' : formatBytes(latestTotals.bytes) }}
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Latest snapshot</CardTitle>
      </CardHeader>
      <CardContent>
        <div v-if="latestLoading" class="text-muted-foreground text-sm">Loading…</div>
        <p
          v-else-if="!latestData?.tables?.length"
          class="text-muted-foreground text-sm"
        >
          No snapshots yet. After the scheduled job runs, data will appear here.
        </p>
        <div v-else class="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Table</TableHead>
                <TableHead class="text-right">Items</TableHead>
                <TableHead class="text-right">Size</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow v-for="row in latestData.tables" :key="row.table_name">
                <TableCell class="font-mono text-xs">{{ row.table_name }}</TableCell>
                <TableCell class="text-right tabular-nums">
                  {{ Intl.NumberFormat().format(Number(row.item_count)) }}
                </TableCell>
                <TableCell class="text-right tabular-nums">
                  {{ formatBytes(Number(row.size_bytes)) }}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>

    <div class="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Items over time</CardTitle>
        </CardHeader>
        <CardContent>
          <div v-if="historyLoading" class="text-muted-foreground text-sm">Loading…</div>
          <p
            v-else-if="historySeries.length === 0"
            class="text-muted-foreground text-sm"
          >
            No history in this range. Run more nightly snapshots to see trends.
          </p>
          <div v-else class="h-[280px]">
            <Line :data="itemsChartData" :options="itemsChartOptions" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Storage over time (GB)</CardTitle>
        </CardHeader>
        <CardContent>
          <div v-if="historyLoading" class="text-muted-foreground text-sm">Loading…</div>
          <p
            v-else-if="historySeries.length === 0"
            class="text-muted-foreground text-sm"
          >
            No history in this range.
          </p>
          <div v-else class="h-[280px]">
            <Line :data="sizeChartData" :options="sizeChartOptions" />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
